import { z } from "zod";
import { DISCOUNT_TYPES, DiscountTypes } from "../constants";

export const DiscountTypeSchema = z.enum(Object.values(DISCOUNT_TYPES) as [...DiscountTypes[]]);

const capitalize = (str: string) => (str.length ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str);

// hex color field -> #RGB / #RGBA / #RRGGBB / #RRGGBBAA, case-insensitive
export const HEX_COLOR_PATTERN = /^#([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i;

export const hexColor = (fieldName: string, defaultValue?: string) => {
    const schema = z
        .string()
        .min(1, { message: capitalize(`${fieldName} color value cannot be empty`) })
        .regex(HEX_COLOR_PATTERN, { message: capitalize(`${fieldName} invalid color input`) });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const fontStyle = (fieldName: string, defaultValue?: string) =>
    requiredString(capitalize(`${fieldName} style is required`), defaultValue);

// font size field -> string or number, coerced to a number and checked against range (defaults to 1-50)
export const fontSize = (
    fieldName: string,
    range: { min: number; max: number } | null = null,
    defaultValue?: number | string,
) => {
    const min = range?.min ?? 0;
    const max = range?.max ?? 50;
    const schema = z.union([z.string(), z.number()]).superRefine((val, ctx) => {
        if (val === "" || val === null || val === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`${fieldName} value cannot be empty`) });
            return;
        }
        const num = Number(val);
        if (Number.isNaN(num)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`${fieldName} invalid font size`) });
            return;
        }
        if (num < min) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`Min: ${min}`) });
            return;
        }
        if (num > max) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`Max: ${max}`) });
        }
    });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const string = (defaultValue?: string) => {
    const schema = z.string();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const number = (defaultValue?: number) => {
    const schema = z.number();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const boolean = (defaultValue?: boolean) => {
    const schema = z.boolean();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const nullableString = (defaultValue?: string | null) => {
    const schema = z.string().nullable();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const nullableNumber = (defaultValue?: number) => {
    const schema = z.number().nullable();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const nullableBoolean = (defaultValue?: boolean) => {
    const schema = z.boolean().nullable();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

// nullable AND optional — the field's key can be absent entirely, and when
// present its value may still be null (e.g. opaque third-party payload data
// like a Shopify variant's weight, which is null for non-physical products
// and can be missing altogether on older records).
export const nullableOptionalString = (defaultValue?: string | null) => {
    const schema = z.string().nullable().optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const nullableOptionalNumber = (defaultValue?: number | null) => {
    const schema = z.number().nullable().optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const requiredString = (message: string, defaultValue?: string) => {
    const schema = z.string().refine((val) => val.trim().length > 0, { message });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const requiredNumber = (message: string, defaultValue?: number) => {
    const schema = z.number({ message }).superRefine((val, ctx) => {
        if (val === null || val === undefined || Number.isNaN(val)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const requiredBoolean = (message: string, defaultValue?: boolean) => {
    const schema = z.boolean({ message }).superRefine((val, ctx) => {
        if (val === null || val === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const optionalString = (defaultValue?: string) => {
    const schema = z.string().optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const optionalNumber = (defaultValue?: number) => {
    const schema = z.number().optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const optionalBoolean = (defaultValue?: boolean) => {
    const schema = z.boolean().optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const requiredArray = <T extends z.ZodTypeAny>(itemSchema: T, minLength: number, message: string) =>
    z.array(itemSchema).min(minLength, message);

export const arrayOf = <T extends z.ZodTypeAny = z.ZodAny>(itemSchema?: T, defaultValue?: z.infer<T>[]) => {
    const schema = z.array(itemSchema ?? (z.any() as unknown as T));
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

// Like arrayOf, but the key itself may be absent (no bare array default —
// pass a defaultValue if you also want the key to heal to a concrete array).
export const optionalArrayOf = <T extends z.ZodTypeAny = z.ZodAny>(itemSchema?: T, defaultValue?: z.infer<T>[]) => {
    const schema = z.array(itemSchema ?? (z.any() as unknown as T)).optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const recordOf = (defaultValue?: Record<string, unknown>) => {
    const schema = z.record(z.string(), z.any());
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const requiredEnum = <T extends [string, ...string[]]>(values: T, message: string, defaultValue?: T[number]) => {
    const schema = z.enum(values, { message });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

// Like requiredEnum, but permissive when the key is absent — no custom
// "required" message, since the field genuinely doesn't need one yet.
export const optionalEnum = <T extends [string, ...string[]]>(values: T, defaultValue?: T[number]) => {
    const schema = z.enum(values).optional();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

// z.coerce.number() + default — for numeric fields fed by form inputs that
// may hand back a string (e.g. an <input type="number"> value).
export const coercedNumber = (defaultValue?: number) => {
    const schema = z.coerce.number();
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const numberInRange = (fieldName: string, range: { min: number; max?: number }, defaultValue?: number) => {
    const { min, max } = range;
    const schema = z.union([z.string(), z.number()]).superRefine((val, ctx) => {
        if (val === "" || val === null || val === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`${fieldName} value cannot be empty`) });
            return;
        }
        const num = Number(val);
        if (Number.isNaN(num)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`${fieldName} must be a valid number`) });
            return;
        }
        if (num < min) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`Min: ${min}`) });
            return;
        }
        if (max !== undefined && num > max) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: capitalize(`Max: ${max}`) });
        }
    });
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const integerInRange = (fieldName: string, range: { min: number; max?: number }, defaultValue?: number) => {
    let schema = z
        .number({ message: capitalize(`${fieldName} is required`) })
        .int(capitalize(`${fieldName} must be a whole number`))
        .min(range.min, capitalize(`Min: ${range.min}`));
    if (range.max !== undefined) {
        schema = schema.max(range.max, capitalize(`Max: ${range.max}`));
    }
    return defaultValue !== undefined ? schema.default(defaultValue) : schema;
};

export const looseArray = () => z.array(z.any()).optional();
export const looseObject = () => z.record(z.string(), z.any()).optional();
export const loose = () => z.any().optional();

export const ImageSchema = z.object({ altText: optionalString(), id: optionalString(), originalSrc: z.string() });
